import { Check, X } from "lucide-react";
import { Logo } from "./Logo";

const ComparisonRow = ({ label, uplevel, franchise, local, independent }: { 
  label: string; 
  uplevel: boolean; 
  franchise?: boolean; 
  local?: boolean; 
  independent?: boolean; 
}) => (
  <tr className="border-b border-border/50 last:border-0">
    <td className="py-4 px-4 text-sm font-medium text-foreground">{label}</td>
    <td className="py-4 px-4 text-center bg-primary/5">
      {uplevel ? <Check className="h-5 w-5 text-primary mx-auto" /> : <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />}
    </td>
    <td className="py-4 px-4 text-center">
      {franchise ? <Check className="h-5 w-5 text-foreground/60 mx-auto" /> : <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />}
    </td>
    <td className="py-4 px-4 text-center">
      {local ? <Check className="h-5 w-5 text-foreground/60 mx-auto" /> : <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />}
    </td>
    <td className="py-4 px-4 text-center">
      {independent ? <Check className="h-5 w-5 text-foreground/60 mx-auto" /> : <X className="h-5 w-5 text-muted-foreground/30 mx-auto" />}
    </td>
  </tr>
);

import { useLocation } from "@/lib/locations";

const features = [
  { label: "Vetted, checked professionals", uplevel: true },
  { label: "COI for HOAs & Property Managers", uplevel: true, franchise: true },
  { label: "Flat-rate upfront pricing", uplevel: true, franchise: true },
  { label: "Background-checked staff", uplevel: true, franchise: true, local: true },
  { label: "Bonded & Insured", uplevel: true, franchise: true, local: true },
  { label: "Eco-Friendly Upgrade Available", uplevel: true, franchise: true },
  { label: "No franchise fees or markups", uplevel: true, local: true, independent: true },
  { label: "Same-cleaner consistency", uplevel: true, local: true, independent: true },
  { label: "Recurring service discounts", uplevel: true, franchise: true },
];

export const Comparison = () => {
  const location = useLocation();
  return (
    <section className="py-24 bg-background">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Why {location.name} Homeowners Choose Uplevel Cleaning</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            See how we compare to other cleaning options in the {location.name} area. We pride ourselves on professional standards and local care.
          </p>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[800px] border-collapse">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="py-6 px-4 text-left font-bold text-lg">Features</th>
                <th className="py-6 px-4 text-center bg-primary/5 rounded-t-2xl">
                  <div className="flex flex-col items-center gap-2">
                    <Logo />
                    <span className="text-[10px] uppercase tracking-widest text-primary font-bold">The Standard</span>
                  </div>
                </th>
                <th className="py-6 px-4 text-center font-semibold text-muted-foreground">Typical Franchise</th>
                <th className="py-6 px-4 text-center font-semibold text-muted-foreground">Small Local Company</th>
                <th className="py-6 px-4 text-center font-semibold text-muted-foreground">Independent Cleaners</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {features.map((f, i) => (
                <ComparisonRow key={i} {...f} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4">
          {features.map((f, i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <div className="text-sm font-semibold text-center mb-4 text-foreground">{f.label}</div>
              <div className="grid grid-cols-4 gap-1 mb-2">
                <div className="text-center">
                  <span className="text-[9px] font-bold text-primary uppercase tracking-wider block">Uplevel</span>
                </div>
                <div className="text-center">
                  <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider block">Franchise</span>
                </div>
                <div className="text-center">
                  <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider block">Local</span>
                </div>
                <div className="text-center">
                  <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider block">Indep.</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1">
                <div className="flex justify-center items-center bg-primary/10 py-2 rounded-lg">
                  {f.uplevel ? <Check className="h-5 w-5 text-primary" /> : <X className="h-5 w-5 text-muted-foreground/30" />}
                </div>
                <div className="flex justify-center items-center py-2">
                  {f.franchise ? <Check className="h-5 w-5 text-foreground/60" /> : <X className="h-5 w-5 text-muted-foreground/30" />}
                </div>
                <div className="flex justify-center items-center py-2">
                  {f.local ? <Check className="h-5 w-5 text-foreground/60" /> : <X className="h-5 w-5 text-muted-foreground/30" />}
                </div>
                <div className="flex justify-center items-center py-2">
                  {f.independent ? <Check className="h-5 w-5 text-foreground/60" /> : <X className="h-5 w-5 text-muted-foreground/30" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
